import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useAuth } from '../../context/AuthContext';
import { Coins, TrendingUp, TrendingDown, Loader2, ArrowUpRight, ArrowDownRight } from 'lucide-react';

export default function PointsPage() {
  const { api } = useAuth();
  const [learners, setLearners] = useState([]);
  const [selectedLearner, setSelectedLearner] = useState('');
  const [ledger, setLedger] = useState([]);
  const [balance, setBalance] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLearners();
  }, []);

  useEffect(() => {
    if (selectedLearner) {
      fetchPointsData();
    }
  }, [selectedLearner]);

  const fetchLearners = async () => {
    try {
      const response = await api.get('/learners/');
      // Handle both paginated (Django) and non-paginated responses
      const learnersList = response.data?.results || response.data || [];
      setLearners(learnersList);
      if (learnersList.length > 0) {
        setSelectedLearner(learnersList[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch learners:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPointsData = async () => {
    try {
      const [balanceRes, ledgerRes] = await Promise.all([
        api.get(`/points/balance/${selectedLearner}/`).catch(() => null),
        api.get(`/points/ledger/${selectedLearner}/`).catch(() => ({ data: [] })),
      ]);
      
      // Try to get balance from API, fallback to learner's points_balance
      const learner = learners.find(l => l.id === selectedLearner);
      const apiBalance = balanceRes?.data?.balance;
      setBalance(apiBalance !== undefined ? apiBalance : (learner?.points_balance || 0));
      setLedger(ledgerRes.data?.results || ledgerRes.data || []);
    } catch (error) {
      console.error('Failed to fetch points data:', error);
      // Fallback to learner's points_balance
      const learner = learners.find(l => l.id === selectedLearner);
      setBalance(learner?.points_balance || 0);
      setLedger([]);
    }
  };

  const selectedLearnerData = learners.find(l => l.id === selectedLearner);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="points-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold">Points</h1>
          <p className="text-muted-foreground">Track earned points and transactions</p>
        </div>
        
        {learners.length > 0 && (
          <Select value={selectedLearner} onValueChange={setSelectedLearner}>
            <SelectTrigger className="w-[200px]" data-testid="learner-select">
              <SelectValue placeholder="Select learner" />
            </SelectTrigger>
            <SelectContent>
              {learners.map(learner => (
                <SelectItem key={learner.id} value={learner.id}>
                  {learner.first_name} {learner.last_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {learners.length === 0 ? (
        <Card className="card-dashboard">
          <CardContent className="py-16 text-center">
            <Coins className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">No learners yet</h2>
            <p className="text-muted-foreground">Add a learner to start tracking points.</p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Balance Card */}
          <Card className="bg-gradient-to-r from-amber-500 to-orange-500 text-white border-0">
            <CardContent className="p-8">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-2xl bg-white/20 flex items-center justify-center">
                  <Coins className="h-8 w-8" />
                </div>
                <div>
                  <p className="text-white/80 text-sm">Current Balance</p>
                  <p className="text-4xl font-bold">{balance} points</p>
                  <p className="text-white/80 text-sm mt-1">
                    {selectedLearnerData?.first_name} {selectedLearnerData?.last_name}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Transaction History */}
          <Card className="card-dashboard">
            <CardHeader>
              <CardTitle>Transaction History</CardTitle>
            </CardHeader>
            <CardContent>
              {ledger.length === 0 ? (
                <div className="text-center py-8">
                  <Coins className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                  <p className="text-muted-foreground">
                    No transactions recorded yet.
                  </p>
                  <p className="text-sm text-muted-foreground mt-1">
                    Points are earned through attendance, achievements, and completing courses!
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {ledger.map((entry, index) => (
                    <div 
                      key={entry.id} 
                      className="flex items-center justify-between p-4 rounded-xl bg-muted/50"
                      data-testid={`transaction-${index}`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          entry.amount > 0 ? 'bg-accent/10 text-accent' : 'bg-destructive/10 text-destructive'
                        }`}>
                          {entry.amount > 0 ? (
                            <ArrowUpRight className="h-5 w-5" />
                          ) : (
                            <ArrowDownRight className="h-5 w-5" />
                          )}
                        </div>
                        <div>
                          <p className="font-medium capitalize">{entry.reason?.replace(/_/g, ' ')}</p>
                          <p className="text-sm text-muted-foreground">
                            {new Date(entry.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <span className={`font-bold text-lg ${
                        entry.amount > 0 ? 'text-accent' : 'text-destructive'
                      }`}>
                        {entry.amount > 0 ? '+' : ''}{entry.amount}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
